// ticketCoverageTheme.js
// Modern, vibrant theme inspired by TicketCoverage.com
// Features purple-to-blue-to-orange gradient design with professional aesthetics

import { createTheme } from '@mui/material/styles';

const ticketCoverageTheme = createTheme({
  palette: {
    primary: {
      main: '#8b5cf6', // Purple/Violet
      light: '#a78bfa',
      dark: '#7c3aed',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#3b82f6', // Blue
      light: '#60a5fa',
      dark: '#2563eb',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#10b981', // Emerald green
      light: '#34d399',
      dark: '#059669',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#f59e0b', // Amber
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#ef4444', // Red
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#06b6d4', // Cyan
      light: '#22d3ee',
      dark: '#0891b2',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FAFAFA',
    },
    text: {
      primary: '#111827', // Gray-900
      secondary: '#4B5563', // Gray-600
    },
    grey: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 800,
      color: '#111827',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      color: '#111827',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      color: '#111827',
    },
    h4: {
      fontWeight: 600,
      color: '#111827',
    },
    h5: {
      fontWeight: 600,
      color: '#111827',
    },
    h6: {
      fontWeight: 600,
      color: '#111827',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#111827',
    },
    subtitle2: {
      fontWeight: 500,
      color: '#4B5563',
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.7,
      color: '#111827',
    },
    body2: {
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#4B5563',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 10,
          padding: '12px 24px',
          fontSize: '16px',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(139, 92, 246, 0.35)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 50%, #f97316 100%)',
          color: '#FFFFFF',
          '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 50%, #ea580c 100%)',
            boxShadow: '0 8px 20px rgba(139, 92, 246, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
          },
        },
        outlined: {
          borderColor: '#8b5cf6',
          color: '#8b5cf6',
          borderWidth: '2px',
          '&:hover': {
            borderColor: '#7c3aed',
            backgroundColor: 'rgba(139, 92, 246, 0.06)',
            borderWidth: '2px',
          },
        },
        text: {
          color: '#8b5cf6',
          '&:hover': {
            backgroundColor: 'rgba(139, 92, 246, 0.06)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#3b82f6',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#8b5cf6',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#8b5cf6',
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          // Custom header styling for TicketCoverage theme
          '&.orange-header-box, &.ticket-coverage-header-box': {
            background: 'linear-gradient(135deg, #1e1b4b 0%, #7c3aed 30%, #3b82f6 60%, #06b6d4 80%, #f97316 100%)',
            color: '#FFFFFF',
            padding: '24px',
            borderRadius: '12px 12px 0 0',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23FFFFFF\' fill-opacity=\'0.08\'%3E%3Ccircle cx=\'50\' cy=\'50\' r=\'3\'/%3E%3Ccircle cx=\'10\' cy=\'10\' r=\'3\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              borderRadius: '12px 12px 0 0',
              pointerEvents: 'none',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.08)',
          border: '1px solid rgba(229, 231, 235, 0.8)',
        },
        elevation1: {
          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.06)',
        },
        elevation2: {
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.08)',
        },
        elevation3: {
          boxShadow: '0 12px 32px rgba(139, 92, 246, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 6px 20px rgba(139, 92, 246, 0.08)',
          border: '1px solid #E5E7EB',
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(249,250,251,1) 100%)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 12px 32px rgba(139, 92, 246, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: '#D1FAE5',
          color: '#059669',
          borderRadius: 10,
          border: '1px solid #A7F3D0',
        },
        standardError: {
          backgroundColor: '#FEE2E2',
          color: '#DC2626',
          borderRadius: 10,
          border: '1px solid #FECACA',
        },
        standardWarning: {
          backgroundColor: '#FEF3C7',
          color: '#D97706',
          borderRadius: 10,
          border: '1px solid #FDE68A',
        },
        standardInfo: {
          backgroundColor: '#DBEAFE',
          color: '#2563EB',
          borderRadius: 10,
          border: '1px solid #BFDBFE',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-root .Mui-completed': {
            color: '#10b981', // Green for completed
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: '#8b5cf6', // Purple for active
          },
          '& .MuiStepConnector-line': {
            borderColor: '#D1D5DB',
          },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: '#10b981',
          },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
            borderColor: '#8b5cf6',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E5E7EB',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#8b5cf6',
          '&.Mui-checked': {
            color: '#8b5cf6',
          },
          '&:hover': {
            backgroundColor: 'rgba(139, 92, 246, 0.08)',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#8b5cf6',
          '&.Mui-checked': {
            color: '#8b5cf6',
          },
          '&:hover': {
            backgroundColor: 'rgba(139, 92, 246, 0.08)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#8b5cf6',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#8b5cf6',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        filled: {
          background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
          color: '#FFFFFF',
        },
        outlined: {
          borderColor: '#8b5cf6',
          color: '#8b5cf6',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: '#E5E7EB',
        },
        bar: {
          borderRadius: 8,
          background: 'linear-gradient(90deg, #8b5cf6 0%, #3b82f6 50%, #06b6d4 100%)',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#8b5cf6',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: '1px solid #E5E7EB',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '8px 0',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': {
            color: '#8b5cf6',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#8b5cf6',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
  },
});

export default ticketCoverageTheme;
